import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { UserProvider, useUser } from './constants/UserContext';
import { usePushNotifications } from './hooks/usePushNotifications';
import { usePresenceHeartbeat } from '@/hooks/usePresenceHeartbeat';
import { ChatUnreadProvider } from '@/contexts/ChatUnreadContext';
import { NotificationBannerHost } from '@/components/common/NotificationBanner';
import { useOAuthDeepLink } from './hooks/useOAuthDeepLink';
import { getRecoveryRedirectSearch } from './utils/recoveryParams';
import { ProtectedRoute } from './components/ProtectedRoute';
// Dev-only preview of the organizer Shifts view with made-up volunteers. The whole
// expression is dead code once import.meta.env.DEV is replaced by false, so a production
// build drops the dynamic import and never emits the chunk.
const ShiftsPreviewPage = import.meta.env.DEV
  ? lazy(() => import('./dev/ShiftsPreviewPage'))
  : null;
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/Login';
import { SignupPage } from './pages/Signup';
import { VolunteerSignupPage } from './pages/Signup/VolunteerSignupPage';
import { OrganizationSignupPage } from './pages/Signup/OrganizationSignupPage';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { ResetPasswordPage } from './pages/ResetPassword';
import { OrganizationsPage } from './pages/Organizations';
import { SettingsPage } from './pages/Settings';
import { CommunicationPage } from './pages/Communication';
import { StorePage } from './pages/Store';
import { ProfilePage } from './pages/Profile';
import { VolunteersPage } from './pages/Volunteers';
import { VolunteerTasksPage } from './pages/VolunteerTasks';
import { NotificationsPage } from './pages/Notifications';
import { OnboardingPage } from './pages/Onboarding';
import { AuthCallbackPage } from './pages/Auth';
import { EventInvitePage } from './pages/EventInvite';
function PushNotificationRegistrar() {
  const { user } = useUser();
  usePushNotifications(user?.id);
  return null;
}

/**
 * Keeps chat_presence current so the notification fan-out can tell someone reading a
 * thread from someone who has the app shut. App-wide, not per chat: the question is
 * about the whole client.
 */
/**
 * Unread counts, provided app-wide: the Communication list and the nav badge -- which is
 * on screen everywhere -- need the same numbers.
 */
function ChatUnreadGate({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  return <ChatUnreadProvider userId={user?.id}>{children}</ChatUnreadProvider>;
}

function PresenceRegistrar() {
  const { user } = useUser();
  usePresenceHeartbeat(user?.id);
  return null;
}

/** Announces a notification that lands while the app is on screen and the push was withheld. */
function NotificationBannerRegistrar() {
  const { user } = useUser();
  return <NotificationBannerHost userId={user?.id} />;
}

function OAuthDeepLinkHandler() {
  useOAuthDeepLink();
  return null;
}

/**
 * Self-heal password-recovery links that land on the wrong route (e.g. the
 * login page at `/`) because Supabase fell back to `site_url`. Forwards the
 * recovery params to `/reset-password` so the reset form can run.
 */
function RecoveryRedirectHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/reset-password') return;
    const search = getRecoveryRedirectSearch(window.location.search, window.location.hash);
    if (search) {
      navigate(`/reset-password${search}`, { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <UserProvider>
          <ChatUnreadGate>
            <PushNotificationRegistrar />
            <PresenceRegistrar />
            <NotificationBannerRegistrar />
            <OAuthDeepLinkHandler />
            <RecoveryRedirectHandler />
            <Routes>
              <Route path="/" element={<LoginPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/auth/callback" element={<AuthCallbackPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/signup/volunteer" element={<VolunteerSignupPage />} />
              <Route path="/signup/organization" element={<OrganizationSignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/organizations"
                element={
                  <ProtectedRoute>
                    <OrganizationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pages/organizations"
                element={
                  <ProtectedRoute>
                    <OrganizationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/communication"
                element={
                  <ProtectedRoute>
                    <CommunicationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/store"
                element={
                  <ProtectedRoute>
                    <StorePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/volunteers"
                element={
                  <ProtectedRoute>
                    <VolunteersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/volunteer-tasks"
                element={
                  <ProtectedRoute>
                    <VolunteerTasksPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <NotificationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invite"
                element={
                  <ProtectedRoute>
                    <EventInvitePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute bypassOnboarding>
                    <OnboardingPage />
                  </ProtectedRoute>
                }
              />
              {ShiftsPreviewPage && (
                <Route
                  path="/dev/shifts"
                  element={
                    <Suspense fallback={null}>
                      <ShiftsPreviewPage />
                    </Suspense>
                  }
                />
              )}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ChatUnreadGate>
        </UserProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
